import { create } from "zustand/react";
import { devtools } from "zustand/middleware";
import { setRequestAuthProvider, type IRequestAuthProvider } from "@/shared/utills/http";
import {
  ensureAzureSession,
  getAzureToken,
  getInternalJwt,
  getMsalAccount,
  initMsal,
  InternalJwtError,
  loginWithMicrosoft,
  logoutFromMicrosoft,
  parseInternalJwt,
  resetInternalJwt,
} from "@/service/auth";
import { IUser } from "./model";

const SIGN_IN_FAILED = "Couldn't sign you in with Microsoft. Try again.";
const SESSION_EXPIRED = "Your session has expired. Please sign in again.";

/** Тексты обмена уже написаны для пользователя; технические ошибки MSAL заменяем общим. */
const toSignInError = (error: unknown) => (error instanceof InternalJwtError ? error.message : SIGN_IN_FAILED);

interface IUserStore {
  user: IUser | null;
  inited: boolean;
  isAuthenticated: boolean;
  /** Почему не удалось войти — показывается на экране логина. */
  error: string | null;
  setUser: (user: IUser | null) => void;
  updateUser: (key: keyof IUser, value: IUser[keyof IUser]) => void;
  /** Старт приложения: MSAL, возврат из Microsoft и тихий обмен токена. Не бросает — ошибка уходит в error. */
  init: () => Promise<void>;
  /** Аккаунт Microsoft уже есть — повторяем обмен на месте, иначе уходим на редирект в Microsoft. */
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useUserStore = create<IUserStore>()(
  devtools(
    (set) => {
      /** Обмен Azure-токена на внутренний JWT; пользователь и его роли (type, teamlead…) — из claims. */
      const authenticate = async () => {
        if (!getMsalAccount()) {
          set({ user: null, isAuthenticated: false, inited: true }, false, "authenticate");
          return;
        }

        try {
          const user = parseInternalJwt<IUser>(await getInternalJwt());

          set({ user, isAuthenticated: true, inited: true, error: null }, false, "authenticate");
        } catch (error) {
          console.error("[Auth] Sign-in failed", error);
          resetInternalJwt();
          set({ user: null, isAuthenticated: false, inited: true, error: toSignInError(error) }, false, "authenticate");
        }
      };

      return {
        user: null,
        inited: false,
        isAuthenticated: false,
        error: null,
        setUser: (user) => set({ user }, false, "setUser"),
        updateUser: (key, value) =>
          set(
            (state) => ({
              user: state.user ? { ...state.user, [key]: value } : null,
            }),
            false,
            "updateUser",
          ),
        init: async () => {
          setRequestAuthProvider(requestAuthProvider);

          try {
            await initMsal();
            await ensureAzureSession();
          } catch (error) {
            console.error("[Auth] MSAL initialization failed", error);
            set({ inited: true, error: toSignInError(error) }, false, "init");
            return;
          }

          await authenticate();
        },
        login: async (returnTo) => {
          set({ error: null }, false, "login");

          if (getMsalAccount()) {
            await authenticate();
            return;
          }

          await loginWithMicrosoft(returnTo);
        },
        logout: async () => {
          resetInternalJwt();
          set({ user: null, isAuthenticated: false, error: null }, false, "logout");
          await logoutFromMicrosoft();
        },
      };
    },
    { name: "UserStore" },
  ),
);

/**
 * Авторизация всех запросов requestApi: бэкенд принимает Azure-токен на любом пути, поэтому
 * внутренний JWT в запросах не участвует — после 401 обновляем только Azure-токен.
 */
const requestAuthProvider: IRequestAuthProvider = {
  getAuthHeaders: async (attempt) => {
    if (!getMsalAccount()) return {};

    return { Authorization: `Bearer ${await getAzureToken({ forceRefresh: attempt === "retry" })}` };
  },
  onUnauthorized: () => {
    // из Microsoft не выходим: на экране логина «Sign in» повторит вход тем же аккаунтом
    resetInternalJwt();
    useUserStore.setState({ user: null, isAuthenticated: false, error: SESSION_EXPIRED }, false, "sessionExpired");
  },
};
